import { Grid, Typography } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import classNames from "classnames";
import { getClassNameVariant } from "components/DIP/helper";
import { useDIPAction } from "components/DIP/hooks";
import { useEnvContext } from "contexts";
import { buyItAllAssets } from "assets/cdn";
import { FeaturesFlags } from "helpers";
import { useAppSelector } from "hooks";
import { useImagePath } from "hooks/useImagePath";
import { DipButton } from "interfaces";
import { Fragment, useState } from "react";
import { useSelector } from "react-redux";
import { selectDipState } from "redux/slices";
import { RootState } from "redux/store";
// import { CardFinalMessage } from "styles/general_styles";
import { CrossedOutText } from "styles/general_styles";
import { CrownIcon, CrownIconV2 } from "utils/svgIcons";
import { getFlexBasis } from "../helper";
import {
	BestValueBadge,
	Button,
	BuyItAllButton,
	BuyItAllItem,
	BuyItAllStyled,
	CustomLoyaltyPointsV2,
	FingerPointer,
	Footer,
	FooterGridItem,
	ItemImage,
	Items,
	LockedOffer,
	LoyaltyPoints,
	LoyaltyValue,
	OfferGridItem,
	RewardAmount,
	RewardItem
} from "./styles";
import { useDIPRewardsV2 } from "components/DIP/hooks/useDIPRewardsV2";
import { sortBundleRewards } from "helpers/rewardSort";

interface BuyItAllProps {}

interface BuyItAllOfferProps {
	item: DipButton;
	index: number;
	offersCount: number; // count of non-final offers
	lockOffers: number;
	onOfferClick: (item: DipButton, index: number, price: number) => void;
	onCancelLock: (index: number) => void;
}

export const BuyItAll = ({}: BuyItAllProps) => {
	const { getImage } = useImagePath("cdn");
	const redBadge = getImage(buyItAllAssets.badge);
	const { currentDip, claimRewardProcess } = useAppSelector(selectDipState);
	const { onAction } = useDIPAction();
	const { loading, claiming } = claimRewardProcess;
	const [lockOffers, setLockOffers] = useState<number>(-1);
	const data = currentDip?.buttons;
	const lastItem = data?.slice(-1)[0];
	const { price, buyType, badgeText, badgeImageUrl } =
		useDIPRewardsV2(lastItem);

	if (!currentDip || !data || data.length === 0) {
		return <Fragment />;
	}

	const allOffersButLast = data.slice(0, -1);

	const onOfferClick = (item: DipButton, index: number, itemPrice: number) => {
		if (lockOffers !== -1 && lockOffers < 3) {
			onAction(item, itemPrice);
		} else {
			setLockOffers(index);
		}
	};

	const onCancelLock = (index: number) => setLockOffers(index);

	const onBuyItAll = (item: DipButton | undefined) => {
		if (!item) {
			return;
		}
		onAction(item, price);
		setLockOffers(3);
	};

	return (
		<BuyItAllStyled
			className={getClassNameVariant(allOffersButLast.length as number)}
		>
			<Grid container spacing={1} justifyContent="center" alignItems="flex-end">
				{allOffersButLast.map((item, i) => (
					<BuyItAllOffer
						key={item.id}
						item={item}
						index={i}
						offersCount={allOffersButLast.length}
						lockOffers={lockOffers}
						onOfferClick={onOfferClick}
						onCancelLock={onCancelLock}
					/>
				))}
			</Grid>
			<Grid item position="relative">
				{badgeText && badgeImageUrl && (
					<BestValueBadge
						className={`buy-all-button ${
							lockOffers !== -1 && lockOffers !== 3 && "blocked-offer"
						}`}
						backgroundImage={redBadge}
					>
						<span>{badgeText}</span>
					</BestValueBadge>
				)}
				<BuyItAllButton
					disabled={loading || claiming}
					onClick={() => onBuyItAll(lastItem)}
					themeColor="tertiary"
				>
					Buy All {price}
					{buyType === "usd" ? " USD" : ""}
				</BuyItAllButton>
			</Grid>
			{/* <BuyItAllFooterText>Only one purchase can be made</BuyItAllFooterText> */}
		</BuyItAllStyled>
	);
};

const BuyItAllOffer: React.FC<BuyItAllOfferProps> = ({
	item,
	index,
	offersCount,
	lockOffers,
	onOfferClick,
	onCancelLock
}) => {
	const { getImage } = useImagePath("cdn");
	const fingerPointerIcon = getImage(buyItAllAssets.fingerPointer);
	const isPortrait = useMediaQuery("(orientation: portrait)");
	const isMobile = isPortrait;
	// Temporarily hidden per product request; restore this state with the JSX below
	// to re-enable the Poi's Bounty messaging.
	// const { canContribute: isPoisActive } = useAppSelector(
	// 	selectPoisBountyV3State
	// );
	const showOfferId = useSelector(
		(state: RootState) => state.debug.showDebugLabels
	);
	const { isFeatureFlagEnabled } = useEnvContext();
	const { claimRewardProcess } = useAppSelector(selectDipState);
	const { loading, claiming } = claimRewardProcess;

	const {
		price,
		buyType,
		buttonLabel,
		rewards,
		loyaltyPointReward,
		loyaltyPointCrossout,
		badgeText,
		badgeImageUrl
	} = useDIPRewardsV2(item);

	return (
		<OfferGridItem item key={item.id} position="relative">
			{lockOffers !== -1 && lockOffers !== index && (
				<div onClick={() => onCancelLock(index)}>
					<LockedOffer />
				</div>
			)}
			<BuyItAllItem
				tier={index}
				className={classNames([
					getClassNameVariant(offersCount as number),
					loyaltyPointReward && "loyalty-reward",
					"item-reward"
				])}
			>
				{badgeText && badgeImageUrl && (
					<BestValueBadge
						className={
							lockOffers !== -1 && lockOffers !== index ? "blocked-offer" : ""
						}
						backgroundImage={badgeImageUrl}
					>
						<span>{badgeText}</span>
					</BestValueBadge>
				)}
				{showOfferId && (
					<Typography
						sx={{
							fontWeight: 700,
							color: "red",
							fontSize: "0.8em",
							height: "auto",
							textAlign: "center"
						}}
					>
						{item.loyaltyOfferId ? item.loyaltyOfferId : item.offerId}
					</Typography>
				)}
				<Items
					tier={index}
					className={classNames([
						getClassNameVariant(offersCount as number),
						"bundle-rewards-item"
					])}
				>
					{sortBundleRewards(rewards).map(({ imageUrl, description }, key) => {
						const flexBasis = getFlexBasis(
							new Array(offersCount) as DipButton[],
							rewards,
							isMobile
						);
						return (
							<RewardItem key={key} width={flexBasis + "%"}>
								<ItemImage src={imageUrl} alt="" />
								<RewardAmount className="description">
									{description}
								</RewardAmount>
							</RewardItem>
						);
					})}
				</Items>
				<Footer
					className={classNames([
						loyaltyPointReward && "loyalty-reward",
						getClassNameVariant(offersCount as number)
					])}
				>
					<Grid container>
						<Grid container spacing={1} alignItems="center">
							<FooterGridItem item>
								{loyaltyPointReward && (
									<>
										{isFeatureFlagEnabled(FeaturesFlags.UI_V2) ? (
											<CustomLoyaltyPointsV2>
												{loyaltyPointCrossout && (
													<CrossedOutText>
														Earn <CrownIconV2 height="0.9em" width="auto" />
														{Number(loyaltyPointCrossout)?.toLocaleString(
															"en-US"
														)}
													</CrossedOutText>
												)}
												<LoyaltyValue>
													Earn <CrownIconV2 height="1.1em" width="auto" />
													{loyaltyPointReward?.toLocaleString("en-US")}
												</LoyaltyValue>
											</CustomLoyaltyPointsV2>
										) : (
											<LoyaltyPoints>
												<div>
													Earn <CrownIcon width="1em" />
													{loyaltyPointReward?.toLocaleString("en-US")}
												</div>
											</LoyaltyPoints>
										)}
									</>
								)}
							</FooterGridItem>
							<FooterGridItem item position="relative">
								{lockOffers === index && (
									<FingerPointer
										src={fingerPointerIcon}
										alt="Press buy again to confirm"
									/>
								)}
								<Button
									disabled={loading || claiming}
									onClick={() => onOfferClick(item, index, price)}
								>
									{!buttonLabel ? (
										<Fragment>
											{buyType === "loyalty_points" && (
												<CrownIcon width="1em" />
											)}
											{price}
											{buyType === "usd" && " USD"}
										</Fragment>
									) : (
										buttonLabel
									)}
								</Button>
							</FooterGridItem>
						</Grid>
						{/*
							{isPoisActive && (
								<Grid item xs={12}>
									<CardFinalMessage
										color="#FBBD05"
										className="with-contrast"
										style={{ fontSize: "0.7em", textAlign: "center" }}
									>
										Contribute to Poi’s Bounty
									</CardFinalMessage>
								</Grid>
							)}
						*/}
					</Grid>
				</Footer>
			</BuyItAllItem>
		</OfferGridItem>
	);
};
